import { css } from '../../../../styled-system/css'
import { createFileRoute, Link } from '@tanstack/react-router'
import { contentWrapper } from '../../../styles/contentWrapper'
import { pageTitle, sectionTitle, sectionWrapper, titleWrapper } from '../../../styles/sectionStyles'
import { input, inputLabel, inputWrapper, requiredFieldLabel } from '../../../styles/inputStyles'
import { Description, Dialog, DialogPanel, DialogTitle, Select } from '@headlessui/react'
import KeyboardArrowDown from '~icons/material-symbols/keyboard-arrow-down.svg'
import ArrowBack from '~icons/material-symbols/arrow-back.svg'
import CheckCircle from '~icons/material-symbols/check-circle.svg'
import { button } from '../../../styles/buttonStyles'
import { useEffect, useState } from 'react'
import { useCreateCompany, useCreateStaff } from '../../../libs/admin/hooks'
import { atom, useAtom } from 'jotai'
import { z } from 'zod'
import { companyInfoSchema } from './-schemas/company-info'
import { ValidatableTextInput, ValidatableTextInputWithParser } from '../../../components/validatable-inputs/text-input'
import toast from 'react-hot-toast'
import { generateRandomNumericCode, generateReadableId, getTenantSlug } from '../../../libs/utils'
import { dialogBackdrop } from '../../../styles/dialogStyles'
import { encodeBankAccountNumber, encodeBankAccountHolderName } from '../../../libs/utils/bank-account-encoder'

export const Route = createFileRoute('/admin/companies/newCompany')({
  component: RouteComponent,
})

function RouteComponent() {
  return <Layout />
}

const staffAccountSchema = z.object({
  slug: z.string().min(1, 'ログインIDを入力してください'),
  password: z.string().min(1, 'パスワードを入力してください'),
})

const companyFormSchema = companyInfoSchema.extend(staffAccountSchema.shape)

type newCompanyType = z.infer<typeof companyFormSchema>

const companyInfoAtom = atom<Partial<newCompanyType>>({})

const BasicInfo = () => {
  const [companyInfo, setCompanyInfo] = useAtom(companyInfoAtom)
  const parseResult = companyFormSchema.safeParse(companyInfo)
  const fieldErrors = parseResult.success ? {} : parseResult.error?.flatten().fieldErrors ?? {}
  return (
    <div className={sectionWrapper()}>
      <h2 className={sectionTitle()}>基本情報</h2>
      <ValidatableTextInput
        title='事業者名'
        placeholder='ポケットコーポレーション'
        value={companyInfo.name ?? ''}
        onChange={(value) => {
          setCompanyInfo((prev) => ({
            ...prev,
            name: value,
          }))
        }}
        isValid={!fieldErrors.name}
        validationMessage={fieldErrors.name?.[0] ?? 'OK'}
        type='string'
        isRequired
      />
      <ValidatableTextInput
        title='登録代表者名'
        placeholder='春日太郎'
        value={companyInfo.ownerName ?? ''}
        onChange={(value) => {
          setCompanyInfo((prev) => ({
            ...prev,
            ownerName: value,
          }))
        }}
        isValid={!fieldErrors.ownerName}
        validationMessage={fieldErrors.ownerName?.[0] ?? 'OK'}
        type='string'
        isRequired
      />
      <ValidatableTextInput
        title='住所'
        placeholder='青空県砂浜市浅瀬区1-1-1'
        value={companyInfo.address ?? ''}
        onChange={(value) => {
          setCompanyInfo((prev) => ({
            ...prev,
            address: value,
          }))
        }}
        isValid={!fieldErrors.address}
        validationMessage={fieldErrors.address?.[0] ?? 'OK'}
        type='string'
        isRequired
      />
      <ValidatableTextInput
        title='電話番号'
        placeholder='0123456789'
        value={companyInfo.contact ?? ''}
        onChange={(value) => {
          setCompanyInfo((prev) => ({
            ...prev,
            contact: value,
          }))
        }}
        isValid={!fieldErrors.contact}
        validationMessage={fieldErrors.contact?.[0] ?? 'OK'}
        type='string'
        isRequired
      />
    </div>
  )
}

const BankAccountInfo = () => {
  const [companyInfo, setCompanyInfo] = useAtom(companyInfoAtom)
  const parseResult = companyFormSchema.safeParse(companyInfo)
  const fieldErrors = parseResult.success ? {} : parseResult.error?.flatten().fieldErrors ?? {}
  
  return (
    <div className={sectionWrapper()}>
      <h2 className={sectionTitle()}>銀行口座情報</h2>
      <ValidatableTextInputWithParser
        title='銀行名'
        placeholder='ﾎﾟｹﾂﾄｷﾞﾝｺｳ'
        value={companyInfo.bankName ?? ''}
        onChange={(value) => {
          setCompanyInfo((prev) => ({
            ...prev,
            bankName: value,
          }))
        }}
        isValid={!fieldErrors.bankName}
        validationMessage={fieldErrors.bankName?.[0] ?? 'OK'}
        type='string'
        parser={encodeBankAccountHolderName}
        formatter={(value) => value}
        isRequired
      />
      <ValidatableTextInputWithParser
        title='金融機関コード'
        placeholder='0000'
        value={companyInfo.bankCode ?? ''}
        onChange={(value) => {
          setCompanyInfo((prev) => ({
            ...prev,
            bankCode: value,
          }))
        }}
        isValid={!fieldErrors.bankCode}
        validationMessage={fieldErrors.bankCode?.[0] ?? 'OK'}
        type='string'
        parser={encodeBankAccountNumber}
        formatter={(value) => value}
        isRequired
      />
      <ValidatableTextInputWithParser
        title='支店名'
        placeholder='ｻｲﾝ'
        value={companyInfo.bankBranchName ?? ''}
        onChange={(value) => {
          setCompanyInfo((prev) => ({
            ...prev,
            bankBranchName: value,
          }))
        }}
        isValid={!fieldErrors.bankBranchName}
        validationMessage={fieldErrors.bankBranchName?.[0] ?? 'OK'}
        type='string'
        parser={encodeBankAccountHolderName}
        formatter={(value) => value}
        isRequired
      />
      <ValidatableTextInputWithParser
        title='支店コード'
        placeholder='000'
        value={companyInfo.bankBranchCode ?? ''}
        onChange={(value) => {
          setCompanyInfo((prev) => ({
            ...prev,
            bankBranchCode: value,
          }))
        }}
        isValid={!fieldErrors.bankBranchCode}
        validationMessage={fieldErrors.bankBranchCode?.[0] ?? 'OK'}
        type='string'
        parser={encodeBankAccountNumber}
        formatter={(value) => value}
        isRequired
      />
      <div className={css(
        inputWrapper.raw(),
        { width: '240px' }
      )}>
        <div className={inputLabel()}>
          口座種別
          <span className={requiredFieldLabel()}>必須</span>
        </div>
        <div className={css({ position: 'relative' })}>
          <Select
            className={css(
              input.raw(),
              { appearance: 'none' }
            )}
            value={companyInfo.bankAccountType}
            onChange={(e) => {
              companyInfoSchema.pick({
                bankAccountType: true,
              }).parse({
                bankAccountType: e.target.value,
              });
              setCompanyInfo((prev) => ({
                ...prev,
                // FIXME: type assertion
                bankAccountType: e.target.value as newCompanyType['bankAccountType'],
              }))
            }}
          >
            <option>普通</option>
            <option>当座</option>
          </Select>
          <KeyboardArrowDown className={css({
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
          })} />
        </div>
      </div>
      <ValidatableTextInputWithParser
        title='口座番号'
        placeholder='0000000'
        value={companyInfo.bankAccountNumber ?? ''}
        onChange={(value) => {
          setCompanyInfo((prev) => ({
            ...prev,
            bankAccountNumber: value,
          }))
        }}
        isValid={!fieldErrors.bankAccountNumber}
        validationMessage={fieldErrors.bankAccountNumber?.[0] ?? 'OK'}
        type='string' 
        parser={encodeBankAccountNumber}
        formatter={(value) => value}
        isRequired
      />
      <ValidatableTextInputWithParser
        title='口座名義'
        placeholder='ｶｽｶﾞﾀﾛｳ'
        value={companyInfo.bankAccountHolderName ?? ''}
        onChange={(value) => {
          setCompanyInfo((prev) => ({
            ...prev,
            bankAccountHolderName: value,
          }))
        }}
        isValid={!fieldErrors.bankAccountHolderName}
        validationMessage={fieldErrors.bankAccountHolderName?.[0] ?? 'OK'}
        type='string'
        parser={encodeBankAccountHolderName}
        formatter={(value) => value}
        isRequired
      />
    </div>
  )
}

const StaffAccountInfo = () => {
  const [companyInfo, setCompanyInfo] = useAtom(companyInfoAtom)
  const parseResult = companyFormSchema.safeParse(companyInfo)
  const fieldErrors = parseResult.success ? {} : parseResult.error?.flatten().fieldErrors ?? {}

  return (
    <div className={sectionWrapper()}>
      <h2 className={sectionTitle()}>事業者アカウント</h2>
      <ValidatableTextInput
        title='ログインID'
        placeholder='tenantslug123456'
        value={companyInfo.slug ?? ''}
        onChange={(value) => {
          setCompanyInfo((prev) => ({
            ...prev,
            slug: value,
          }))
        }}
        isValid={!fieldErrors.slug}
        validationMessage={fieldErrors.slug?.[0] ?? 'OK'}
        type='text'
        isRequired
      />
      <ValidatableTextInput
        title='パスワード'
        placeholder='00000000'
        value={companyInfo.password ?? ''}
        onChange={(value) => {
          setCompanyInfo((prev) => ({
            ...prev,
            password: value,
          }))
        }}
        isValid={!fieldErrors.password}
        validationMessage={fieldErrors.password?.[0] ?? 'OK'}
        type='text'
        isRequired
      />
    </div>
  )
}

const Layout = () => {
  const [companyInfo, setCompanyInfo] = useAtom(companyInfoAtom)
  const tenantSlug = getTenantSlug()
  const [companyId, setCompanyId] = useState('')
  const { mutateAsync: createCompany, isPending: isPendingCreateCompany } = useCreateCompany()
  const {
    mutateAsync: mutateAsyncCreateStaff,
    isPending: isPendingCreateStaff,
  } = useCreateStaff()
  const [isStoreDialogOpen, setIsStoreDialogOpen] = useState(false)
  // reset companyInfo when the component mounts
  useEffect(() => {
    setCompanyInfo({
      slug: `${tenantSlug}${generateRandomNumericCode(6)}`,
      password: generateReadableId(12),
    })
  }, [setCompanyInfo, tenantSlug])

  const navigate = Route.useNavigate()

  const parsedCompanyInfo = companyFormSchema.safeParse(companyInfo)
  const canSubmit = parsedCompanyInfo.success
  const handleCloseAndNavigateAway = (targetCompanyId: string) => {
    if (!targetCompanyId) {
      return
    }
    navigate({
      to: '/admin/companies/$companyId',
      params: {
        companyId: targetCompanyId,
      },
    })
  }
  const handleSubmit = async () => {
    if (!parsedCompanyInfo.success) {
      return
    }
    const { slug, password, ...companyPayload } = parsedCompanyInfo.data
    let createdCompanyId = ''
    try {
      const id = await createCompany(companyPayload)
      createdCompanyId = id
      setCompanyId(id)
      await mutateAsyncCreateStaff({
        companyId: id,
        slug,
        password,
      })
      toast.success('事業者を登録し、ログイン情報を発行しました')
      setIsStoreDialogOpen(true)
    } catch (error) {
      console.error('Error creating company or staff account:', error)
      if (createdCompanyId) {
        toast.error('アカウントの発行に失敗しました。事業者詳細画面から再度お試しください。')
        handleCloseAndNavigateAway(createdCompanyId)
      } else {
        toast.error('事業者の登録に失敗しました')
      }
    }
  }

  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>
          <Link to='/admin/companies'>
            <ArrowBack />
          </Link>
          事業者登録
        </h1>
      </div>
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      })}>
        <BasicInfo />
        <BankAccountInfo />
        <StaffAccountInfo />
        <button
          type='submit'
          className={button({ color: 'primary' })}
          onClick={handleSubmit}
          disabled={!canSubmit || isPendingCreateStaff || isPendingCreateCompany}
        >
          登録
        </button>

        {/* Dialog for confirmation */}
        <Dialog
          open={isStoreDialogOpen}
          onClose={() => handleCloseAndNavigateAway(companyId)}
          className={dialogBackdrop()}
        >
          <DialogPanel className={css({
            backgroundColor: 'white',
            width: '320px',
            margin: 'auto',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          })}>
            <CheckCircle
              className={css({
                width: '96px',
                height: '96px',
                color: 'text.accentSuccess',
                margin: '0 auto',
              })}
            />
            <DialogTitle
              className={css({
                fontSize: '16px',
                textAlign: 'center',
              })}
            >事業者を登録しました</DialogTitle>
            <Description
              className={css({
                fontSize: '16px',
                textAlign: 'center',
              })}
            >続けて店舗を登録しますか?</Description>
            <div className={css({
              display: 'flex',
              justifyContent: 'center',
              gap: '8px',
            })}>
              <button
                type='button'
                className={button({ color: 'ghost' })}
                onClick={() => handleCloseAndNavigateAway(companyId)}
              >
                あとで登録
              </button>
              <Link
                to='/admin/companies/$companyId/newStores'
                params={{
                  companyId: companyId,
                }}
              >
                <button
                  type='button'
                  className={button({ color: 'primary' })}
                  onClick={() => {
                    setIsStoreDialogOpen(false)
                  }}
                >
                  店舗を登録する
                </button>
              </Link>
            </div>
          </DialogPanel>
        </Dialog >
      </div>
    </div>
  )
}
